import { useEffect, useState } from 'react';
import { fetchUser } from '../../../store/user/async-action';
import AuthServices from '../../services/auth.services';
import { useStateDispatch } from '../../../store';
import { useHistory, useLocation } from 'react-router';
import { setToken } from '../../../helper/token';
import { axiosConfigEvent } from '../../../utils/events';
import AuthApis from '../configs/auth.apis';
import apiManager from '../../../utils/apiManager';
import { Eye, EyeOff } from 'lucide-react';
import LoginDto from '../models/login.dto';
import AuthRoutes from '../../configs/auth.route';
import { useMutation } from 'react-query';
import { useSnackbar } from 'notistack';
import { KYCService } from '../../../services/kyc';
import { useTimedInterval } from '../../../hooks/useTimedInterval';
import { useTranslation } from 'react-i18next';

/** Used for login  */
export const useLoginHooks = () => {
  const [loginDto, setLoginDto] = useState<LoginDto>(new LoginDto({}));
  const EyeIcon = loginDto.isPasswordVisible ? EyeOff : Eye;
  //
  const history = useHistory();
  const dispatch = useStateDispatch();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const isWithout2FA = urlSearchParams.get('disable2FA') === '1';
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const handleLogin = async () => {
    if (loginDto.isValid) {
      setIsLoginLoading(true);
      await apiManager
        .post(
          isWithout2FA ? AuthApis.loginWithout2FA : loginDto.loginApi,
          loginDto.toJson()
        )
        .then((response) => {
          if (isWithout2FA) {
            setToken(response.data.access, response.data.refresh);
            // Calling refreshConfig will set the axios interceptor in UserSessionManager component.
            // Which will add the default token and also will add the interceptor for refreshing the token.
            axiosConfigEvent.refreshConfig();
            dispatch(fetchUser());
            return;
          }
          history.push({
            pathname: '/verify-otp',
            state: {
              verification_string: response.data.verification_string,
              email: loginDto.emailOrNationalId,
              password: loginDto.password,
              isVerifyBySms: loginDto.isVerifyBySms,
            },
          });
        })
        .catch((err) => {})
        .finally(() => setIsLoginLoading(false));
    }
  };

  return {
    loginDto,
    setLoginDto,
    EyeIcon,
    isLoginLoading,
    handleLogin,
  };
};

/** Used for verify the otp */
export const useVerifyOtpHooks = () => {
  const [otp, setOtp] = useState('');
  // Dependecy
  const history = useHistory();
  const dispatch = useStateDispatch();
  const { state } = useLocation() as any;
  const verificationString = state?.verification_string;
  const isVerifyBySms = state?.isVerifyBySms;

  useEffect(() => {
    if (!verificationString) history.push('/login');
  }, [verificationString]);

  // Handle login by verifying the OTP
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const handleVerifyOtp = async () => {
    setIsVerifyingOtp(true);
    const token = await AuthServices.verifyLoginOtp(otp, verificationString);
    if (token) dispatch(fetchUser());
    setIsVerifyingOtp(false);
  };

  return {
    otp,
    setOtp,
    isVerifyBySms,
    verificationString,
    isVerifyingOtp,
    handleVerifyOtp,
  };
};

/** Used for login  */
export const useResetPasswordHooks = () => {
  const [emailDto, setEmailDto] = useState<LoginDto>(new LoginDto({}));

  //
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const handleSendLink = async () => {
    if (emailDto.isValidEmailNid) {
      setIsLoading(true);
      try {
        await apiManager.post(
          '/api/system-users/password-reset-link/',
          emailDto.toResetPasswordJson()
        );
        history.push(AuthRoutes.LOGIN);
      } catch (error) {
        //
        setIsLoading(false);
      }
    }
  };

  return {
    emailDto,
    setEmailDto,
    isLoading,
    handleSendLink,
  };
};

/** Used for registration */
export const useRegistrationHooks = () => {
  const [nationalId, setNationalId] = useState('');
  const signUpMutation = useMutation(KYCService.signUpWithNationalID);
  const { enqueueSnackbar } = useSnackbar();
  const [randomNumber, setRandomNumber] = useState(null);
  const history = useHistory();
  const { t } = useTranslation();

  const { startInterval, cancelInterval } = useTimedInterval({
    callback: (data) =>
      KYCService.poolSignUpCallback(data).then((res) => {
        if (!res.data.token) return;
        cancelInterval();
        history.push(`/register/?id_token=${res.data.token}`);
      }),
    delay: 1000,
    triggerCount: 60,
    onLastTrigger: () => {
      enqueueSnackbar(t('auth.session_expired'), { variant: 'error' });
      setRandomNumber(null);
      signUpMutation.reset();
    },
  });

  const onSignUpClick = () => {
    signUpMutation.mutate(nationalId, {
      onSuccess(res) {
        if (res.data.random) {
          setRandomNumber(res.data.random);
          startInterval(res.data.transId);
        } else {
          enqueueSnackbar(t('common.something_went_wrong'), { variant: 'error' });
        }
      },
      onError(err: any) {
        enqueueSnackbar(
          err.response?.data?.error ||
            err.response?.data?.national_id?.[0] ||
            t('common.something_went_wrong'),
          { variant: 'error' }
        );
      },
    });
  };
  return {
    nationalId,
    setNationalId,
    onSignUpClick,
    randomNumber,
    signUpMutation,
  };
};
